import Cookies from "js-cookie";
import { getFullYear } from "./date";
import {
  BIKE_COMPREHENSIVE_PLAN,
  BIKE_TP_PLAN
} from "@utils/bike/bikeConstants";
import dateFormat from "date-fns/format";
import { DateFormats } from "@utils/date";
import { TP_PLANS } from "@utils/journeyEnum";
import {
  standardPlans,
  getLowestFirstLookPlans
} from "@components/common/plans/utils/helper";
import { isEmpty } from "@utils/lodash";
import { getFromSessionStorage } from "@utils/handleSessionStorage";
import { captureExceptions } from "@utils/sentry";

export const sevenDaysMilliSeconds: number = 7 * 24 * 60 * 60 * 1000;

export function getInrFormat(number: string | number, symbol = false): string {
  if (!number) {
    return "";
  }
  number = Math.round(Number(number));
  const y = `${number}`.split(".");
  const x = y[0].toString();
  let lastThree = x.substring(x.length - 3);
  const otherNumbers = x.substring(0, x.length - 3);
  if (otherNumbers !== "") lastThree = `,${lastThree}`;
  let result = otherNumbers.replace(/\B(?=(\d{2})+(?!\d))/g, ",") + lastThree;
  if (symbol) result = `₹${result}`;
  return result;
}

export function convertToLakh(number: string | number) {
  return (
    Math.round(
      ((typeof number === "string" ? parseInt(number, 10) : number) / 100000) *
        100
    ) / 100
  );
}

export const webEngageLogin = (phone: string) => {
  if (window.webengage && window.webengage.user) {
    if (typeof window.webengage.user.login === "function") {
      window.webengage.user.login(phone);
    }
    if (typeof window.webengage.user.setAttribute === "function") {
      window.webengage.user.setAttribute("we_phone", phone);
    }
  }
};

export const getAnonymousId = () => {
  let anonymousId = "";
  const aidFormCookie = Cookies.get("we_aid");
  if (aidFormCookie) {
    anonymousId = aidFormCookie;
  } else if (
    window.webengage &&
    window.webengage.user &&
    typeof window.webengage.user.getAnonymousId === "function"
  ) {
    if (window.webengage.user.getAnonymousId()) {
      const aid = window.webengage.user.getAnonymousId();
      Cookies.set("we_aid", aid);
      anonymousId = aid;
    }
  }
  return anonymousId;
};

export const pageView = (path: string) => {
  if (window.parent.location !== window.location) {
    window.parent.dataLayer.push({
      event: "Pageview",
      url: path
    });
  } else {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "Pageview",
      url: path
    });
  }
};

export function goToError(action) {
  if (action.payload === 420) {
    window.location.href = `/not-available`;
  } else if (action.payload === 400) {
    window.location.href = `${window.basePath}/login/?next=${window.location.pathname}${window.location.search}`;
  } else if (action.payload === 421) {
    window.location.href = `${window.basePath}/renewals/receive-call-message`;
  } else {
    window.location.href = `${window.basePath}/renewals/custom-error`;
  }
}

export const getPlanName = selectedPlan => {
  const isSmartSaver =
    selectedPlan?.id?.includes("deductible") &&
    !selectedPlan?.id?.includes("acko_garage");
  const isSuperSaver = selectedPlan?.id?.includes("acko_garage");
  const planName = isSmartSaver
    ? "smartSaver"
    : isSuperSaver
    ? "superSaver"
    : null;

  return planName;
};

export const getNudgeAddonData = () => {
  const nudgeAddon = JSON.parse(getFromSessionStorage("nudgeAddon") || "[]");
  return nudgeAddon || [];
};

export const getTagLineIndex = () => {
  const nudgeAddon = getNudgeAddonData();
  if (nudgeAddon?.includes("pa_owner_car")) {
    return 1;
  }
  return 0;
};

export const getValidCovers = () => {
  const validCovers = JSON.parse(getFromSessionStorage("validCovers") || "{}");
  return validCovers || [];
};

export const getUniquePreselectedAddon = preAddon => {
  const preSelectedAddon: any = [];
  (preAddon || []).map(each => {
    if (preSelectedAddon.indexOf(each.id) === -1) {
      preSelectedAddon.push(each.id);
    }
  });

  return preSelectedAddon?.map(addonId => {
    return {
      id: addonId
    };
  });
};

export const getUniquedAddons = addonList => {
  const addons: any = [];
  (addonList || []).map(each => {
    if (addons.indexOf(each.id) === -1) {
      addons.push(each.id);
    }
  });

  return addons;
};

export const showAddonNudge = (selected, total) => {
  if (selected <= 0 && total === 1) {
    return true;
  } else if (total <= 1) {
    return false;
  } else if (total === 2 && selected > 1) {
    return false;
  } else if (total === 3 && selected >= 2) {
    return false;
  } else if (selected > Math.ceil(total / 2)) {
    return false;
  } else {
    return true;
  }
};

export const getRenewalDateSticky = expiryDate => {
  const expiryTime = new Date(
    `${dateFormat(new Date(expiryDate), DateFormats.ISO_DATE)}T05:30:00+05:30`
  ).getTime();

  const currentTime = new Date(
    `${dateFormat(new Date(), DateFormats.ISO_DATE)}T05:30:00+05:30`
  ).getTime();

  return (
    expiryTime <= currentTime ||
    currentTime + sevenDaysMilliSeconds >= expiryTime
  );

  // return (
  //   (expiryTime > currentTime &&
  //     currentTime + sevenDaysMilliSeconds > expiryTime) ||
  //   expiryTime === currentTime
  // );
};

export const getDateDiff = expiryDate => {
  const currentTime = new Date().getTime();
  const expiry = new Date(expiryDate).getTime();

  return Math.ceil((expiry - currentTime) / (24 * 60 * 60 * 1000));
};

export const checkIfBikeJourney = product => {
  return product === "bike";
};

export const getTpPlan = product => {
  return `${product}_tp`;
};

export const getCompStandardPlan = product => {
  return `${product}_comprehensive`;
};

export const getBikePriceDiff = ({ plansData, tenure }) => {
  const compPlanPrice = plansData
    ?.find(plan => plan?.id?.includes("comprehensive"))
    ?.tenures?.filter(t => t.tenure == tenure)
    ?.map(p => p.discountedPrice)?.[0];

  const tpPlanPrice = plansData
    ?.find(plan => plan?.id?.includes("bike_tp"))
    ?.tenures?.filter(t => t.tenure == tenure)
    ?.map(p => p.discountedPrice)?.[0];

  return compPlanPrice - tpPlanPrice;
};

export const getCarPriceDiff = plansData => {
  return (
    plansData?.find(plan => plan?.id === "car_comprehensive")?.price
      ?.net_premium -
    plansData?.find(plan => plan?.id === "car_tp")?.price?.net_premium
  );
};

export const checkIfPaCoverPreSelected = ({
  registrationYear,
  previousPolicyId
}) => {
  return (
    previousPolicyId?.includes(BIKE_TP_PLAN) ||
    (previousPolicyId?.includes(BIKE_COMPREHENSIVE_PLAN) &&
      getFullYear() - parseInt(registrationYear) >= 10)
  );
};

export const getAddonsText = addOn => {
  return addOn?.length > 1 ? "Add-ons" : "Add-on";
};

export const checkIfThirdPartyPlan = plan => {
  return TP_PLANS?.includes(plan);
};

export const checkIfComprehensivePlan = plan => {
  return plan?.includes("comprehensive");
};

export const getZdPlanToBeUpdated = ({ selectedPlanId, plansData }) => {
  let zdPlan: any = null;
  if (selectedPlanId === standardPlans?.comp) {
    const availableZdPlan = plansData.find(
      plan => plan.id === standardPlans?.zd
    );
    zdPlan = availableZdPlan;
  } else if (selectedPlanId === standardPlans?.od) {
    const availableZdPlan = plansData.find(
      plan => plan.id === standardPlans?.zd_od
    );
    zdPlan = availableZdPlan;
  } else if (selectedPlanId?.includes("comprehensive_deductible")) {
    const filteredPlans = getLowestFirstLookPlans(plansData);
    const availableZdPlan = filteredPlans.find(plan =>
      plan.id.includes("zero_dep")
    );
    zdPlan = availableZdPlan;
  }

  if (!isEmpty(zdPlan)) {
    return zdPlan?.id;
  }

  return null;
};

// the following mapping just gives the type of previous year plan. It is not the exact mapping of all the different types of plans
export const previousPolicyPlanTypes = {
  od: "od",
  zd: "zero_dep",
  comp: "comprehensive",
  tp: "car_tp",
  bundled: "car_bundled",
  bikebundled: "bike_bundled"
};

export const planUnavailabilityContent = {
  comp: {
    cardContent:
      "We are unable to offer you a Comprehensive plan due to limited services in your location. We're constantly working to improve our services and hope to be able to serve you in the future.",
    modalContent: null,
    modalHeading: null
  },
  tp: {
    cardContent: null,
    modalContent: null,
    modalHeading: null
  },
  zd: {
    cardContent:
      "We are unable to offer you a Zero Depreciation plan due to limited services in your location.",
    modalContent: null,
    modalHeading: null
  },
  od: {
    cardContent:
      "We recommend you a Comprehensive plan as your Own Damage and Third Party covers are up for renewal.",
    modalContent: [
      "When you purchased your new car, your insurance policy included a Third Party plan (covers damage caused by your car to others). In addition, you may have purchased an Own Damage plan to protect your car against accidents, theft, natural calamities etc.",
      "We are offering you a Comprehensive Plan (includes Third Party and Own Damage coverages) as both your plans are up for renewal."
    ],
    modalHeading: "Why do we recommend a comprehensive plan?"
  },
  bundled: {
    cardContent:
      "You are eligible for an <b>Own Damage</b> plan as you already have an active Third Party plan.",
    modalContent: [
      "Your previous car policy includes a Third Party coverage (covers damage caused by your car to others) for three years, and an Own Damage coverage (covers damage caused to your car) for one year.",
      "We’re offering you only Own Damage coverage, as it is due for renewal."
    ],
    modalHeading: "Why are you eligible for Own Damage only plan?"
  },
  bikeod: {
    cardContent:
      "You are eligible for an <b>Own Damage</b> plan as you already have an active Third Party plan.",
    modalContent: [
      "Your previous bike policy includes a Third Party coverage for five years (covers damage caused by your bike to others), and an Own Damage coverage (covers damage caused to your bike) for one year.",
      "We’re offering you only Own Damage coverage, as it is due for renewal."
    ],
    modalHeading: "Why are you eligible only for an Own Damage plan?"
  }
};

export const isComprehensiveStandardPlanEligible = ({ plansData, product }) => {
  return !isEmpty(
    plansData?.find(item => item.id === getCompStandardPlan(product))
  );
};

export const shouldRenderPlanNudge = ({
  selectedPlanId,
  product,
  plansData
}: {
  selectedPlanId: string;
  product: string;
  plansData: any;
}) => {
  const isCurrentPlanComprehensive = () => {
    return selectedPlanId?.includes("comprehensive");
  };

  const isComprehensiveStandardPlanEligible = () => {
    return !isEmpty(
      plansData?.find(item => item.id === getCompStandardPlan(product))
    );
  };

  // const isZeroDepPlanEligible = () => {
  //   return plansData?.filter(item => item.id.includes("zero_dep"))?.length > 0;
  // };

  const isZeroDepStandardPlanEligible = () => {
    return !isEmpty(plansData?.find(item => item.id === standardPlans?.zd));
  };

  const isZeroDepDeductiblePlanEligible = () => {
    return (
      plansData?.filter(item => item.id.includes("zero_dep_deductible"))
        ?.length > 0
    );
  };

  const isZdOdPlanEligible = () => {
    return !isEmpty(plansData?.find(item => item.id === standardPlans?.zd_od));
  };

  // tp => comp standard
  // comp standard => zd standard
  // comp supersaver => zd supersaver
  // od => od_zd
  return (
    (checkIfThirdPartyPlan(selectedPlanId) &&
      isComprehensiveStandardPlanEligible()) ||
    (!checkIfBikeJourney(product) &&
      ((isCurrentPlanComprehensive() &&
        ((selectedPlanId === standardPlans?.comp &&
          isZeroDepStandardPlanEligible()) ||
          (selectedPlanId?.includes("comprehensive_deductible") &&
            isZeroDepDeductiblePlanEligible()))) ||
        (selectedPlanId === standardPlans?.od && isZdOdPlanEligible())))
  );
};

// this function compares the addons in the previous policy and currently selected plan
export const getEligibleForPreviousAddon = (
  prevoiusYearAddon: { id: string }[] | undefined,
  addons: { id: string }[] | undefined
): boolean | null => {
  try {
    const prevPlanAddonId = prevoiusYearAddon?.map(adon => adon?.id);
    const currentPlanAddonId = addons?.map(adon => adon?.id);
    prevPlanAddonId?.sort();
    currentPlanAddonId?.sort();
    const areCurrentAdddonsSameAsLastYear =
      JSON.stringify(prevPlanAddonId) === JSON.stringify(currentPlanAddonId);
    return areCurrentAdddonsSameAsLastYear;
  } catch (error) {
    // returning null in case of any error in segment data
    captureExceptions(error, null);
    window?.newrelic.noticeError(error, {
      name: "getEligibleForPreviousAddon error in renewal utils"
    });
    return null;
  }
};

export const agentSharedUtmSource = [
  "advisor_share_quote_whatsapp",
  "advisor_share_quote_sms"
];

export const IS_INFO_TEXT = "infotext";

export const ADDONS_CONTAINER = "ADDONS";

export const CONSUMABLES = "consumables";

export const MINIFIED_DETAILS = "minifiedDetails";

export const ROUND_TO_1_DECIMAL = "roundto1decimal";

export const appendPlanIfNotPresent = planName => {
  if (!planName?.includes("Plan")) {
    planName += " Plan";
  }
  return planName;
};

export const formatAddonsRenewal = addons => {
  const priorityAddons: any[] = [];
  const otherAddons: any[] = [];
  const selectedAddons: any[] = [];

  addons?.forEach(addon => {
    if (addon?.id === "pa_owner_car" && !addon?.is_selected) {
      priorityAddons.push(addon);
    } else if (addon?.is_selected) {
      selectedAddons.push(addon);
    } else {
      otherAddons.push(addon);
    }
  });

  otherAddons.sort((a, b) => a?.price?.net_premium - b?.price?.net_premium);

  return [...priorityAddons, ...otherAddons, ...selectedAddons];
};

export const getParentPlanName = planDisplayName => {
  return planDisplayName?.includes("Zero Depreciation")
    ? "Zero Depreciation"
    : "Comprehensive";
};
